import type {
  UaePassClientType,
  UaePassFlowResult,
  UaePassStatus,
} from '@tabbio/types';
import {
  AppAuthSessionPayload,
  applyAppAuthSession,
  exchangeAppAuthSession,
  mergeAuthenticatedUser,
} from './app-auth';
import {
  normalizeEmailStatusPayload,
  type RawEmailStatusPayload,
} from './auth-email-status';
import { api } from './api';
import { markUaePassNativeAuthSheetCleanupPending } from './uae-pass-session';

export interface EmailStatusPayload {
  exists: boolean;
  hasPassword: boolean;
  canAuthenticate: boolean;
  status: string;
  suggestedFlow: 'sign-in' | 'sign-up';
  /** True when the account owns an active partner account (partners.tabbio.com). */
  isPartner: boolean;
}

export interface LinkedAccountPayload {
  id: string;
  providerId: string;
  accountId: string;
  scopes: string[];
  createdAt: string;
  updatedAt: string;
  userId: string;
}

/** Providers `startSocialAccountLink` / `unlinkSocialAccount` accept. */
export type LinkableSocialProvider = 'google' | 'apple';

export interface SocialLinkStartPayload {
  authorizeUrl: string;
  /** The callback the API registered; used as the in-app browser return URL. */
  callbackURL: string;
  provider: LinkableSocialProvider;
}

export interface UaePassAuthorizeUrlPayload {
  authorizeUrl: string;
  clientType: UaePassClientType;
  redirectUri: string;
  state: string;
  uiLocales?: 'en' | 'ar';
}

function assertResponseData<T>(
  response: {
    data?: T | null;
    error?: { code?: string; message?: string } | null;
    meta?: { uaePassLogoutUrl?: unknown } | null;
  },
  fallback: string,
) {
  if (response.error || !response.data) {
    const error = new Error(response.error?.message || fallback) as Error & {
      code?: string;
      uaePassLogoutUrl?: string;
    };
    error.code = response.error?.code;
    if (typeof response.meta?.uaePassLogoutUrl === 'string') {
      error.uaePassLogoutUrl = response.meta.uaePassLogoutUrl;
    }
    throw error;
  }

  return response.data;
}

async function applySessionFromPayload(payload: AppAuthSessionPayload) {
  await applyAppAuthSession(payload);
  return payload;
}

async function applySessionFromUaePassResult(result: UaePassFlowResult) {
  if (result.status !== 'authenticated') {
    return result;
  }

  markUaePassNativeAuthSheetCleanupPending();
  await applySessionFromPayload({
    accessToken: result.accessToken,
    accessTokenExpiresAt: result.accessTokenExpiresAt,
    refreshToken: result.refreshToken,
    user: {
      ...result.user,
      image: null,
      status: 'active',
      isOnboardingCompleted: Boolean(result.user.isOnboardingCompleted),
      isUaePassVerified: Boolean(result.user.isUaePassVerified),
    },
  });

  return result;
}

export async function exchangeAuthTokens() {
  return exchangeAppAuthSession();
}

export async function checkEmailStatus(email: string) {
  const response = await api.post<RawEmailStatusPayload>(
    '/api/auth/email-status',
    { email },
    { requiresAuth: false, retryOnUnauthorized: false },
  );

  return normalizeEmailStatusPayload(
    assertResponseData(response, 'Unable to check email'),
  );
}

export async function listLinkedAccounts() {
  const response = await api.get<LinkedAccountPayload[]>(
    '/api/auth/linked-accounts',
  );

  return assertResponseData(response, 'Unable to load linked accounts');
}

/**
 * Starts "connect Google/Apple" from Settings.
 *
 * Goes through the API rather than `authClient.linkSocial()` because the app
 * drops the Better Auth cookie once it has exchanged it for app tokens, so the
 * client-side call would be unauthenticated. The API bridges with a transient
 * session and hands back the provider's authorization URL.
 */
export async function startSocialAccountLink(input: {
  provider: LinkableSocialProvider;
  clientType: 'native' | 'web';
  nativeScheme?: string;
}) {
  const response = await api.post<SocialLinkStartPayload>(
    '/api/auth/linked-accounts/link',
    input,
  );

  return assertResponseData(response, 'Unable to start the connection');
}

export async function unlinkSocialAccount(input: {
  providerId: LinkableSocialProvider;
  accountId?: string;
}) {
  const response = await api.post<{
    providerId: LinkableSocialProvider;
    unlinked: boolean;
  }>('/api/auth/linked-accounts/unlink', input);

  return assertResponseData(response, 'Unable to disconnect that account');
}

export async function setInitialPassword(newPassword: string) {
  const response = await api.post<AppAuthSessionPayload>(
    '/api/auth/set-initial-password',
    { newPassword },
  );

  return applySessionFromPayload(
    assertResponseData(response, 'Unable to set password'),
  );
}

export async function updateAccountName(name: string) {
  const response = await api.post<{ id: string; email: string; name: string; image: string | null; username?: string | null; status: string; isOnboardingCompleted: boolean; isUaePassVerified: boolean }>(
    '/api/auth/account/name',
    { name: name.trim() },
  );

  const user = assertResponseData(response, 'Unable to update name');
  mergeAuthenticatedUser(user);
  return user;
}

export async function updateAccountUsername(username: string) {
  const response = await api.post<{ id: string; email: string; name: string; image: string | null; username?: string | null; status: string; isOnboardingCompleted: boolean; isUaePassVerified: boolean }>(
    '/api/auth/account/username',
    { username: username.trim().toLowerCase() },
  );

  const user = assertResponseData(response, 'Unable to update username');
  mergeAuthenticatedUser(user);
  return user;
}

export async function sendSignupOtp(email: string) {
  const response = await api.post<{ status: boolean }>(
    '/api/auth/signup/send-otp',
    { email: email.trim().toLowerCase() },
    { requiresAuth: false, retryOnUnauthorized: false },
  );

  return assertResponseData(response, 'Unable to send verification code');
}

export async function verifySignupOtp(email: string, otp: string) {
  const response = await api.post<{ status: boolean }>(
    '/api/auth/signup/verify-otp',
    { email: email.trim().toLowerCase(), otp: otp.trim() },
    { requiresAuth: false, retryOnUnauthorized: false },
  );

  return assertResponseData(response, 'Invalid verification code');
}

export async function requestEmailChangeOtp(newEmail: string) {
  const response = await api.post<{ status: boolean }>(
    '/api/auth/account/email/request-change',
    { newEmail: newEmail.trim().toLowerCase() },
  );

  return assertResponseData(response, 'Unable to send verification code');
}

export async function confirmEmailChangeOtp(newEmail: string, otp: string) {
  const response = await api.post<AppAuthSessionPayload>(
    '/api/auth/account/email/confirm-change',
    { newEmail: newEmail.trim().toLowerCase(), otp: otp.trim() },
  );

  return applySessionFromPayload(
    assertResponseData(response, 'Unable to update email'),
  );
}

export async function changeAccountPassword(
  currentPassword: string,
  newPassword: string,
) {
  const response = await api.post<AppAuthSessionPayload>(
    '/api/auth/account/password/change',
    { currentPassword, newPassword },
  );

  return applySessionFromPayload(
    assertResponseData(response, 'Unable to change password'),
  );
}

export async function getUaePassAuthorizeUrl(params: {
  clientType: UaePassClientType;
  acrValues?: string;
  redirectUri?: string;
  uiLocales?: 'en' | 'ar';
}) {
  const searchParams = new URLSearchParams({
    clientType: params.clientType,
  });

  if (params.acrValues) {
    searchParams.set('acrValues', params.acrValues);
  }

  if (params.redirectUri) {
    searchParams.set('redirectUri', params.redirectUri);
  }

  if (params.uiLocales) {
    searchParams.set('uiLocales', params.uiLocales);
  }

  const response = await api.get<UaePassAuthorizeUrlPayload>(
    `/api/auth/uae-pass/authorize-url?${searchParams.toString()}`,
    { requiresAuth: false, retryOnUnauthorized: false },
  );

  return assertResponseData(response, 'Unable to start UAE PASS');
}

export async function authenticateWithUaePass(params: {
  clientType: UaePassClientType;
  code: string;
  state: string;
  redirectUri: string;
}) {
  const response = await api.post<UaePassFlowResult>(
    '/api/auth/uae-pass',
    params,
    { requiresAuth: false, retryOnUnauthorized: false },
  );

  return applySessionFromUaePassResult(
    assertResponseData(response, 'Unable to authenticate with UAE PASS'),
  );
}

export async function createUaePassAccount(linkingToken: string) {
  const response = await api.post<UaePassFlowResult>(
    '/api/auth/uae-pass/create-account',
    { linkingToken },
    { requiresAuth: false, retryOnUnauthorized: false },
  );

  return applySessionFromUaePassResult(
    assertResponseData(response, 'Unable to create account with UAE PASS'),
  );
}

export async function linkUaePass(params: {
  clientType?: UaePassClientType;
  code?: string;
  state?: string;
  redirectUri?: string;
  linkingToken?: string;
}) {
  const response = await api.post<{
    message: string;
    uaePassData: UaePassStatus['uaePassData'];
  }>('/api/auth/uae-pass/link', params);

  const result = assertResponseData(response, 'Unable to link UAE PASS');
  mergeAuthenticatedUser({ isUaePassVerified: true });
  return result;
}

export async function unlinkUaePass() {
  const response = await api.post<{ message: string }>(
    '/api/auth/uae-pass/unlink',
    {},
  );

  const result = assertResponseData(response, 'Unable to unlink UAE PASS');
  mergeAuthenticatedUser({ isUaePassVerified: false });
  return result;
}

export async function getUaePassStatus() {
  const response = await api.get<UaePassStatus>('/api/auth/uae-pass/status');
  return assertResponseData(response, 'Unable to load UAE PASS status');
}
